import { filter, get, last, map, sumBy, takeRight, flatten, defaultTo, size, cloneDeep, reduce, findIndex, uniq, forEach } from 'lodash';
import { proto } from '../../proto/AsrProto';
import { safeStringify } from './general';
import { captureWarnMessage } from '../../tracker/raven';
import { getEnabledASRVendor } from '../../store/selectors/asr';
import { recLogger } from '../logger/loggerFactory';
import { EXPLICIT_INTEGRATION_SOURCE, KEYPHRASE_GROUP_NAME_FOR_START, KEYPHRASE_GROUP_NAME_FOR_STOP } from '../../constants/backend';
import type {
    AsrMicResults,
    AsrMicResultHistoryItem,
    AsrRecognitionConfig,
    AsrRawResults,
    AsrRawVendorResult,
    AsrSpottingConfig,
    AsrVoiceMetrics,
    TrainerPhraseGroup
} from '../../store/types';
import type { TranscriptItem } from '../transcript';

export const HISTORY_LENGTH = 500;
type AnyRecord = Record<string, any>;
type SpottingCounterMap = Record<string, number>;

interface SpottedLists {
    whitelist: SpottingCounterMap;
    blacklist: SpottingCounterMap;
}

function mergeKWObjects(obj: AnyRecord = {}, intoObj: AnyRecord = {}) {
    const iter = (result: AnyRecord, value: number, key: string) => ({
        ...result,
        [key]: defaultTo(result[key], 0) + value
    });
    return reduce(obj, iter, { ...intoObj });
}

export function mergeSpottedLists(initial: Partial<SpottedLists> = {}, target: Partial<SpottedLists> = {}): SpottedLists {
    return {
        whitelist: mergeKWObjects(initial.whitelist, target.whitelist),
        blacklist: mergeKWObjects(initial.blacklist, target.blacklist)
    };
}

export function kwObjectToList(obj: SpottingCounterMap) {
    return reduce(obj, (result: { text: string; count: number }[], count: number, text: string) => result.concat({ text, count }), []);
}

export function refreshPreviousVendorResult(currentVendorResults?: AsrRawVendorResult) {
    const def: AsrRawVendorResult = { wordsPerMinute: null };
    const partial: AsrRawVendorResult = {};
    return Object.assign(defaultTo(currentVendorResults, def), partial);
}

export function getSpottedPhrases(spottedResult: AnyRecord = {}): SpottedLists {
    const result: SpottedLists = {
        whitelist: {},
        blacklist: {}
    };
    forEach(spottedResult.whitelist, (phrase: AnyRecord) => {
        phrase.segments.forEach((segment: string) => {
            result.whitelist[segment] = 1;
        });
    });

    forEach(spottedResult.blacklist, (phrase: AnyRecord) => {
        phrase.segments.forEach((segment: string) => {
            result.blacklist[segment] = 1;
        });
    });

    return result;
}

export function setResult(vendorsResults: AnyRecord, asrResult: AnyRecord) {
    const { final, partial } = asrResult;

    return {
        ...vendorsResults,
        wordsPerMinute: get(final, 'alternatives[0].wordsPerMinute') || get(partial, 'wordsPerMinute')
    };
}

function getWordConfidence(alternative: AnyRecord, normWord: AnyRecord, words: AnyRecord[]) {
    if (!words.length) {
        console.warn(`Alternative words: ${safeStringify(alternative.words)}`);
        console.warn(`Word: ${safeStringify(normWord)}`);
        captureWarnMessage('There are no words to calculate confidence');
        return 0;
    }
    return sumBy(words, (item: AnyRecord) => item.validationResult.validationScore) / words.length;
}

export function getTimelineTranscript(result: AnyRecord) {
    const alternative = result.final.alternatives[0];

    const preparedWords = alternative.normWords.map((normWord: AnyRecord) => {
        const createPronunciation = (phone: AnyRecord) => ({
            sound: phone.phone.split('_')[0],
            confidence: phone.validationResult.validationScore
        });
        const words = alternative.words.slice(normWord.span.begin, normWord.span.end);
        const phonesList = map(words, 'phones');
        const pronunciation = flatten(map(phonesList, (phones: AnyRecord[]) => map(phones, createPronunciation)));

        return {
            normalizedWord: normWord.word,
            words,
            confidence: getWordConfidence(alternative, normWord, words as AnyRecord[]),
            punctuation: normWord.punctuation,
            pronunciation
        };
    });

    return {
        normalizedTranscript: alternative.normalizedTranscript,
        startOffset: result.timings.utteranceStartTimeMsec ? result.timings.utteranceStartTimeMsec : 0,
        endOffset: result.timings.currentTimeOffsetMsec,
        words: preparedWords
    };
}

const selectNormalizedTranscript = (data: AnyRecord) => get(data, 'final.alternatives[0].normalizedTranscript');

export function processTextResult(asrResult: AnyRecord, previousVendorsResult: AnyRecord) {
    return {
        ...setResult(refreshPreviousVendorResult(previousVendorsResult), asrResult),
        timeOffset: asrResult.timings.currentTimeOffsetMsec
    };
}

function buildTimelineTranscript(asrResult: AnyRecord) {
    const normalizedTranscript = selectNormalizedTranscript(asrResult);

    if (size(normalizedTranscript) > 0) {
        return getTimelineTranscript(asrResult);
    }

    return null;
}

export function processAsrMessage(asrResult: AnyRecord, storeRawData: AnyRecord) {
    const vendor = proto.Vendor[asrResult.vendor].toLowerCase();
    const vendorResults = processTextResult(asrResult, storeRawData[vendor]);

    return {
        updatedResults: {
            speechRate: vendorResults.wordsPerMinute,
            timeOffset: vendorResults.timeOffset
        },
        currentResults: {
            ...storeRawData,
            [vendor]: vendorResults
        }
    };
}

export function validateSpottingConfig(spottingConfig: AnyRecord) {
    const transform = (group: AnyRecord) => {
        return {
            ...group,
            head: uniq(group.head),
            complement: uniq(group.complement)
        };
    };

    return {
        ...spottingConfig,
        whitelist: spottingConfig.whitelist.map(transform),
        blacklist: spottingConfig.blacklist.map(transform)
    };
}

export function getSpottingConfigTotalLength(spottingConfig: AnyRecord[]) {
    return spottingConfig.reduce((acc: number, item: AnyRecord) => item.phrases.length + acc, 0);
}

function mergeLists(currentList: AnyRecord[], updateList: AnyRecord[], type: string | null = null) {
    const findItemIndex = (list: AnyRecord[], item: AnyRecord) => {
        const itemText = item.text.toLowerCase();
        const comparator = ({ text }: { text: string }) => text.toLowerCase() === itemText;
        return findIndex(list, comparator);
    };
    const update = (list: AnyRecord[]) => (item: AnyRecord) => {
        const index = findItemIndex(list, item);
        Object.assign(item, { count: get(list[index], 'count', 0) });
    };

    const result = cloneDeep(currentList);

    if (type === 'whitelist') {
        result.forEach(({ alternatives }: AnyRecord) => alternatives.forEach(update(updateList)));
        result.forEach(({ complements = [] }) => complements.forEach(update(updateList)));
    } else {
        result.forEach(update(updateList));
    }

    return result;
}

function calculatePhraseGroupCount(whitelist: AnyRecord[]) {
    return whitelist.map((phraseGroup: AnyRecord) => {
        const result = phraseGroup;
        result.count = sumBy(phraseGroup.alternatives, 'count');
        return result;
    });
}

function updateHistory(resultsHistory: AsrMicResultHistoryItem[], newResults: AsrMicResults, timeOffset?: number) {
    const history = takeRight(resultsHistory, HISTORY_LENGTH - 1);
    return history.concat({ ...newResults, timeOffset });
}

export function updateTranscript(timelineTranscripts: TranscriptItem[], type: string, asrResult: AnyRecord) {
    const item = buildTimelineTranscript(asrResult);
    if (item != null) {
        return defaultTo(timelineTranscripts, []).concat({ type, ...item });
    }
    return timelineTranscripts;
}

function combineResults(
    micResults: AsrMicResults,
    {
        speechRate,
        whitelist,
        blacklist
    }: { speechRate?: number | null; whitelist: { text: string; count: number }[]; blacklist: { text: string; count: number }[] }
): AsrMicResults {
    return {
        ...micResults,
        speechRate: speechRate ?? null,
        whitelist: calculatePhraseGroupCount(mergeLists(micResults.whitelist, whitelist, 'whitelist')) as TrainerPhraseGroup[],
        blacklist: mergeLists(micResults.blacklist, blacklist) as TrainerPhraseGroup[]
    };
}

export function updateMicResults(
    state: { micResults: AsrMicResults; micResultsRaw: AsrRawResults | null; micResultsHistory: AsrMicResultHistoryItem[] },
    asrResult: AnyRecord
) {
    if (!asrResult) {
        return { micResultsRaw: null as AsrRawResults | null };
    }

    const asrData = processAsrMessage(asrResult, cloneDeep(state.micResultsRaw || {})) as {
        updatedResults: { speechRate?: number | null; timeOffset?: number };
        currentResults: AsrRawResults;
    };
    const { timeOffset, speechRate } = asrData.updatedResults;
    const micResults: AsrMicResults = { ...state.micResults, speechRate: speechRate ?? null };

    const micResultsHistory = updateHistory(state.micResultsHistory, micResults, timeOffset);

    return {
        micResults,
        micResultsRaw: asrData.currentResults,
        micResultsHistory
    };
}

export function updatePhraseSpottingResults(
    state: {
        config: { asr?: AsrRecognitionConfig | null };
        micResults: AsrMicResults;
        micResultsRaw: AsrRawResults | null;
        micResultsHistory: AsrMicResultHistoryItem[];
    },
    spotResult: AnyRecord
) {
    if (!spotResult) {
        return { micResultsRaw: null as AsrRawResults | null };
    }

    let vendor;

    try {
        [vendor] = getEnabledASRVendor(get(state, 'config.asr'));
    } catch {
        recLogger.error(`Failed to update phrase spotting results, ASR vendor not resolved`);
        return;
    }

    const spotPhrases = getSpottedPhrases(spotResult);
    const storeRawData = cloneDeep(state.micResultsRaw || {}) as AsrRawResults;
    const previousVendorsResult = storeRawData[vendor];
    const newVendorResults = refreshPreviousVendorResult(previousVendorsResult);
    const spotResults = {
        finalSpotted: mergeSpottedLists(newVendorResults.finalSpotted, spotPhrases),
        partialSpotted: mergeSpottedLists(newVendorResults.partialSpotted, spotPhrases)
    };
    const { whitelist, blacklist } = mergeSpottedLists(spotResults.finalSpotted, spotResults.partialSpotted);

    const asrData = {
        updatedResults: {
            whitelist: kwObjectToList(whitelist),
            blacklist: kwObjectToList(blacklist)
        },
        currentResults: {
            ...storeRawData,
            [vendor]: { ...previousVendorsResult, ...spotResults }
        }
    };
    const micResults = combineResults(state.micResults, asrData.updatedResults);
    const micResultsHistory = updateHistory(state.micResultsHistory, micResults);

    return {
        micResults,
        micResultsRaw: asrData.currentResults,
        micResultsHistory
    };
}

export function prepareWhiteList(spottingConfig: AsrSpottingConfig) {
    const phrases = get(spottingConfig, 'whitelist', []);

    const iter = (groups: AnyRecord, { groupName, points, head, complement }: AnyRecord) => {
        const next: AnyRecord = {
            ...groups,
            [groupName]: groups[groupName] || {
                text: groupName,
                count: 0,
                alternatives: [],
                complements: []
            }
        };
        head.forEach((text: string) => {
            next[groupName].alternatives.push({ text, points, count: 0 });
        });
        complement.forEach((text: string) => {
            next[groupName].complements.push({ text, points, count: 0 });
        });
        return next;
    };

    return Object.values(reduce(phrases, iter, {} as AnyRecord)) as TrainerPhraseGroup[];
}

export function prepareBlackList(spottingConfig: AsrSpottingConfig) {
    const phrases = get(spottingConfig, 'blacklist', []);

    const iter = (groups: AnyRecord, { head }: AnyRecord) => {
        const next = {
            ...groups
        };
        head.forEach((text: string) => {
            next[text] = {
                text,
                count: 0,
                alternatives: [],
                complements: []
            };
        });
        return next;
    };

    return Object.values(reduce(phrases, iter, {} as AnyRecord)) as TrainerPhraseGroup[];
}

export function getDefaultMicResults(spottingConfig: AsrSpottingConfig): AsrMicResults {
    return {
        speechRate: null,
        whitelist: prepareWhiteList(spottingConfig),
        blacklist: prepareBlackList(spottingConfig)
    };
}

export function onCallEnded(
    state: {
        sessionStartOffsetMsec?: number;
        config: { spottingConfig: AsrSpottingConfig | null };
        micResultsHistory: AsrMicResultHistoryItem[];
        timelineTranscripts: TranscriptItem[] | null;
    },
    payload: { sessionEndOffsetMsec?: number }
): {
    micResults: AsrMicResults;
    timelineTranscripts: TranscriptItem[];
} {
    const sessionEndOffsetMsec = payload.sessionEndOffsetMsec ?? 0;
    const sessionStartOffsetMsec = state.sessionStartOffsetMsec ?? 0;
    const callDuration = sessionEndOffsetMsec - sessionStartOffsetMsec;
    const micResults =
        last(
            filter(state.micResultsHistory, (item: AsrMicResultHistoryItem) => {
                return !item.timeOffset || item.timeOffset <= callDuration;
            })
        ) || getDefaultMicResults(state.config.spottingConfig || { whitelist: [], blacklist: [] });
    const timelineTranscripts = filter(state.timelineTranscripts || [], (item: TranscriptItem) => item.startOffset < callDuration);
    return { ...state, micResults, timelineTranscripts };
}

export const updateVoiceMetrics = (
    metrics: AsrVoiceMetrics,
    payload: { speechRatio?: number | null; loudness?: number | null }
): AsrVoiceMetrics => ({
    ...metrics,
    speechRatio: payload.speechRatio ?? null,
    loudness: payload.loudness ?? null
});

/**
 * saveRealtimeCallChecker returns a boolean value indicating if a call data must be saved
 * based on the real-time % setting
 */
export const saveRealtimeCallChecker = (realtimeOnlyCallsPercent = 0) => {
    if (realtimeOnlyCallsPercent === 0) {
        return true;
    }

    const modifier = Math.abs(realtimeOnlyCallsPercent);
    const rng = Math.floor(Math.random() * 100);

    return rng > modifier;
};

export const normalizeSpottingConfig = (spottingConfig: AnyRecord) => {
    return {
        whitelist: spottingConfig.whitelist.map((p: AnyRecord) => ({
            ...p,
            visible: true
        })),
        blacklist: spottingConfig.blacklist.map((p: AnyRecord) => ({
            ...p,
            visible: true
        }))
    };
};

export const isKeyphraseIntegrationMode = (appConfig: AnyRecord) => {
    return appConfig?.callDetection?.explicitIntegrationSource === EXPLICIT_INTEGRATION_SOURCE.KEYPHRASE_AUDIO_EVENT;
};

export const pathSpottingConfigMode = (spottingConfig: AnyRecord, isKeyphraseIntegration: boolean) => {
    return {
        whitelist: isKeyphraseIntegration
            ? spottingConfig.whitelist.map((phrase: AnyRecord) => {
                  if (phrase.groupName === KEYPHRASE_GROUP_NAME_FOR_STOP || phrase.groupName === KEYPHRASE_GROUP_NAME_FOR_START) {
                      return {
                          ...phrase,
                          head: phrase.head.filter((p: string) => p !== phrase.groupName),
                          points: 0,
                          visible: false
                      };
                  }
                  return phrase;
              })
            : spottingConfig.whitelist.filter((phrase: AnyRecord) => {
                  // filter out Keyphrase Integration Keywords
                  return ![KEYPHRASE_GROUP_NAME_FOR_START, KEYPHRASE_GROUP_NAME_FOR_STOP].includes(phrase.groupName);
              }),
        blacklist: spottingConfig.blacklist
    };
};
